import { TokenKind } from '../../json/tokens';
import type { RepairContext, RepairProposal, RepairRule } from '../types';

const BOM = '\uFEFF';

/**
 * Drop a leading byte-order mark.
 *
 * A document that starts with U+FEFF makes `JSON.parse` throw, so this really is a defect
 * and not cosmetics. The legacy
 * fixer declared the same rule and it could never fire: it ran after `trim()`, and
 * `String.prototype.trim` counts U+FEFF as whitespace, so the BOM was always gone before the
 * rule looked (recon §2.2). Ours fires because it asks the scanner, which reports the BOM as
 * `Unknown@0+1` and does not strip it (`json/lexer.ts`).
 *
 * Only offset 0 counts. A U+FEFF anywhere else is a zero-width no-break space — a legitimate
 * character that belongs to whoever typed it.
 */
export const stripBomRule: RepairRule = {
  id: 'strip-bom',
  confidence: 'safe',
  priority: 0,
  resolves: ['encoding/bom'],

  detect(ctx: RepairContext): readonly RepairProposal[] {
    const first = ctx.tokens[0];
    if (first === undefined) return [];
    if (first.kind !== TokenKind.Unknown) return [];
    if (first.from !== 0 || first.to !== 1) return [];
    if (ctx.source.charAt(0) !== BOM) return [];

    return [
      {
        ruleId: 'strip-bom',
        span: { from: 0, to: 1 },
        edits: [{ from: 0, to: 1, insert: '' }],
        labelCode: 'fix.strip-bom',
        resolves: 'encoding/bom',
      },
    ];
  },
};
